import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  getBytes,
  getStorage,
  ref,
  uploadBytesResumable,
  type FirebaseStorage
} from "firebase/storage";
import { auth, firebaseApp } from "../firebase/client";
import { firebaseEnv } from "../config/env";

const REMEMBERED_BUCKET_KEY = "mundely_personal_documents_bucket_v1";
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const BUCKET_UNREACHABLE_CODES = new Set([
  "storage/retry-limit-exceeded",
  "storage/unknown",
  "storage/bucket-not-found",
  "storage/project-not-found"
]);

export interface SnapshottedDocumentFile {
  blob: Blob;
  name: string;
  mimeType: string;
  size: number;
}

export interface PersonalDocumentStorageUpload {
  storagePath: string;
  url: string;
  bucket: string;
}

export class PersonalDocumentStorageError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "PersonalDocumentStorageError";
    this.code = code;
  }
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
  heic: "image/heic",
  heif: "image/heif",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  rtf: "application/rtf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet"
};

function usableMime(value: unknown): boolean {
  const mime = String(value || "").trim().toLowerCase();
  return Boolean(mime && mime !== "file" && mime !== "application/octet-stream" && mime.includes("/"));
}

export function resolveDocumentMime(fileName: unknown, declaredType: unknown): string {
  if (usableMime(declaredType)) return String(declaredType);
  const name = String(fileName || "");
  const dot = name.lastIndexOf(".");
  if (dot >= 0) {
    const ext = name.slice(dot + 1).toLowerCase();
    if (MIME_BY_EXTENSION[ext]) return MIME_BY_EXTENSION[ext];
  }
  return "application/octet-stream";
}

function configuredBucket(): string {
  return String(firebaseEnv.storageBucket || "").replace(/^gs:\/\//, "").trim();
}

function alternateBucket(bucket: string): string {
  if (bucket.endsWith(".firebasestorage.app")) {
    return bucket.slice(0, -".firebasestorage.app".length) + ".appspot.com";
  }
  if (bucket.endsWith(".appspot.com")) {
    return bucket.slice(0, -".appspot.com".length) + ".firebasestorage.app";
  }
  return "";
}

function rememberedBucket(): string {
  try {
    return String(localStorage.getItem(REMEMBERED_BUCKET_KEY) || "");
  } catch {
    return "";
  }
}

function rememberBucket(bucket: string): void {
  try {
    if (bucket) localStorage.setItem(REMEMBERED_BUCKET_KEY, bucket);
  } catch {
    // Local cache is optional.
  }
}

export function personalDocumentBucketCandidates(): string[] {
  const primary = configuredBucket();
  const candidates = [rememberedBucket(), primary, alternateBucket(primary)];
  const seen = new Set<string>();
  return candidates.filter((item) => {
    if (!item || seen.has(item)) return false;
    seen.add(item);
    return true;
  });
}

const storageCache: Record<string, FirebaseStorage> = {};

function storageFor(bucket: string): FirebaseStorage {
  if (storageCache[bucket]) return storageCache[bucket];
  const instance = getStorage(firebaseApp, `gs://${bucket}`);
  try {
    (instance as any).maxUploadRetryTime = 30000;
    (instance as any).maxOperationRetryTime = 20000;
  } catch {
    // Best effort only.
  }
  storageCache[bucket] = instance;
  return instance;
}

function safeName(name: string): string {
  return String(name || "document")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .slice(0, 120) || "document";
}

function storagePaths(uid: string, documentId: string, originalName: string): string[] {
  const clean = safeName(originalName);
  return [
    `users/${uid}/documents/${documentId}/${clean}`,
    `users/${uid}/profile/documents/${documentId}/${clean}`
  ];
}

function errorCode(error: unknown): string {
  return String((error as { code?: string } | null)?.code || "storage/unknown");
}

function isBucketUnreachable(code: string): boolean {
  return BUCKET_UNREACHABLE_CODES.has(code);
}

export function snapshotDocumentFile(file: File): Promise<SnapshottedDocumentFile> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onerror = () => reject(new PersonalDocumentStorageError("read-failed", "Lettura del file non riuscita."));
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        if (!buffer || !buffer.byteLength) {
          reject(new PersonalDocumentStorageError("read-failed", "File vuoto o illeggibile."));
          return;
        }
        const mimeType = resolveDocumentMime(file.name, file.type);
        const blob = new Blob([buffer], { type: mimeType });
        resolve({ blob, name: file.name, mimeType, size: blob.size });
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      reject(error);
    }
  });
}

function uploadOne(
  storage: FirebaseStorage,
  storagePath: string,
  blob: Blob,
  originalName: string,
  mimeType: string
): Promise<void> {
  const task = uploadBytesResumable(ref(storage, storagePath), blob, {
    contentType: mimeType,
    customMetadata: {
      purpose: "personal-document",
      originalName: originalName.slice(0, 200)
    }
  });
  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      undefined,
      reject,
      () => resolve()
    );
  });
}

export async function uploadPersonalDocumentBlob(
  uid: string,
  documentId: string,
  blob: Blob,
  originalName: string,
  mimeType: string
): Promise<PersonalDocumentStorageUpload> {
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== uid) {
    throw new PersonalDocumentStorageError("auth/user-mismatch", "Sessione non valida per il caricamento.");
  }
  if (!blob.size) throw new PersonalDocumentStorageError("empty-file", "File vuoto.");
  if (blob.size > MAX_UPLOAD_BYTES) throw new PersonalDocumentStorageError("too-large", "File troppo grande.");
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new PersonalDocumentStorageError("offline", "Serve una connessione per caricare il documento.");
  }

  await currentUser.getIdToken(true);

  const buckets = personalDocumentBucketCandidates();
  if (!buckets.length) {
    throw new PersonalDocumentStorageError("no-bucket", "Nessun bucket Firebase Storage configurato.");
  }

  const paths = storagePaths(uid, documentId, originalName);
  const attempts: string[] = [];
  let lastError: unknown = null;

  for (const bucket of buckets) {
    const storage = storageFor(bucket);
    let bucketUnreachable = false;

    for (const storagePath of paths) {
      attempts.push(`${bucket}:${storagePath}`);
      try {
        await uploadOne(storage, storagePath, blob, originalName, mimeType);
        const remoteRef = ref(storage, storagePath);
        const metadata = await getMetadata(remoteRef);
        if (Number(metadata.size) !== blob.size) throw new PersonalDocumentStorageError("storage/size-mismatch", "Dimensione cloud non valida.");
        // Read back the actual cloud bytes. Required to avoid invisible files or
        // false successes that failed in the previous Android Test builds.
        const readBack = await getBytes(remoteRef, MAX_UPLOAD_BYTES + 1);
        const expected = new Uint8Array(await blob.arrayBuffer());
        const actual = new Uint8Array(readBack);
        if (actual.length !== expected.length || actual.some((value,index) => value !== expected[index])) {
          throw new PersonalDocumentStorageError("storage/content-mismatch", "Il file cloud non corrisponde all'originale.");
        }
        const url = await getDownloadURL(remoteRef);
        rememberBucket(bucket);
        return { storagePath, url, bucket };
      } catch (error) {
        lastError = error;
        const code = errorCode(error);
        if (isBucketUnreachable(code)) {
          bucketUnreachable = true;
          break;
        }
        // Permission failures can depend on the path. Try the next known compatible path.
        if (code === "storage/unauthorized" || code === "storage/object-not-found") continue;
        // Other failures are unlikely to improve by changing path.
        break;
      }
    }

    if (!bucketUnreachable && lastError && !["storage/unauthorized", "storage/object-not-found"].includes(errorCode(lastError))) {
      break;
    }
  }

  const code = errorCode(lastError);
  throw new PersonalDocumentStorageError(
    code,
    `Caricamento Firebase Storage non riuscito (${code}). Tentativi: ${attempts.join(" | ")}`
  );
}

export async function deletePersonalDocumentBlob(storagePath: string): Promise<boolean> {
  if (!storagePath || storagePath.startsWith("firestore")) return false;
  let lastError: unknown = null;
  for (const bucket of personalDocumentBucketCandidates()) {
    try {
      await deleteObject(ref(storageFor(bucket), storagePath));
      rememberBucket(bucket);
      return true;
    } catch (error) {
      lastError = error;
      const code = errorCode(error);
      if (code === "storage/object-not-found") return true;
      if (!isBucketUnreachable(code)) break;
    }
  }
  if (lastError) console.warn("Personal document delete failed", errorCode(lastError));
  return false;
}
